import React, { useEffect, useMemo, useState } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import './Analytics.css';
import './GeneralAnalytics.css';
import './SurveyAnalytics.css';

const GENERAL_ANALYTICS_REPORT_VERSION = 1;
const SURVEY_SERVICE_TEST_CONFIG = {
  totalQuestions: 5,
  onlyPhoneCalls: true,
  showCallType: false,
};
const SURVEY_SCRIPT_CONFIG = {
  'survey service test': SURVEY_SERVICE_TEST_CONFIG,
  'opinion service test': SURVEY_SERVICE_TEST_CONFIG,
  'voxco test 11/12': {
    totalQuestions: 10,
    questionOrder: ['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6', 'Q7', 'Q8', 'Q9', 'Q10'],
    onlyPhoneCalls: false,
    showCallType: true,
  },
};

export const SURVEY_REPORT_CSV_OPTIONS = [
  {
    key: 'surveyStats',
    label: 'Download Stats CSV',
    filePrefix: 'survey-stats',
  },
  {
    key: 'surveyResponses',
    label: 'Download Responses CSV',
    filePrefix: 'survey-responses',
  },
];

const CHART_COLORS = ['#0ea5e9', '#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6'];

const normalizeScriptName = (value) => String(value || '').trim().toLowerCase();

const getSurveyScriptConfig = (scriptName) =>
  SURVEY_SCRIPT_CONFIG[normalizeScriptName(scriptName)] || null;

const formatQuestionLabel = (value) => {
  const str = String(value ?? '').trim();
  if (!str) return 'Answer';
  if (/^Q/i.test(str)) return `Q${str.replace(/^Q/i, '')}`;
  if (/^\d+$/.test(str)) return `Q${str}`;
  return str;
};

const escapeCsvField = (value) => {
  const str = value === null || value === undefined ? '' : String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildPercent = (count, total) => {
  if (!total) return 0;
  return Math.round((count / total) * 1000) / 10;
};

const getCallTypeLabel = (log) => {
  const callId = String(log?.callSid || log?.callId || '');
  if (!callId) return 'Unknown';
  if (callId === 'voiceAgent') return 'Voice agent';
  if (callId === 'chatbot') return 'Chatbot';
  if (callId.startsWith('CA')) return 'Phone call';
  return 'Unknown';
};

const compareQuestionStats = (left, right) => {
  const leftNumber = Number(String(left?.questionNumber || '').replace(/^Q/i, ''));
  const rightNumber = Number(String(right?.questionNumber || '').replace(/^Q/i, ''));
  if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left?.question || '').localeCompare(String(right?.question || ''));
};

export const buildSurveyQuestionOrder = (scriptName, surveyStats) => {
  const scriptConfig = getSurveyScriptConfig(scriptName);
  if (scriptConfig?.questionOrder?.length) return scriptConfig.questionOrder;
  if (!surveyStats) return [];

  return Object.values(surveyStats)
    .sort((a, b) => {
      const aNum = Number(String(a.questionNumber || '').replace(/^Q/i, ''));
      const bNum = Number(String(b.questionNumber || '').replace(/^Q/i, ''));
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
      return String(a.question || '').localeCompare(String(b.question || ''));
    })
    .map((qStat) => qStat.questionNumber || qStat.question);
};

const buildBatchStatsCsv = (stats) => {
  const rows = [['Question Number', 'Question', 'Answer', 'Count', 'Percent of Question']];

  Object.values(stats || {})
    .sort(compareQuestionStats)
    .forEach((qStat) => {
      const total = qStat.total || 0;
      Object.entries(qStat.answers || {})
        .sort((a, b) => b[1] - a[1])
        .forEach(([answer, count]) => {
          rows.push([
            qStat.questionNumber || '',
            qStat.question || '',
            answer,
            count,
            `${buildPercent(count, total)}%`,
          ]);
        });
    });

  return rows.map((cols) => cols.map(escapeCsvField).join(',')).join('\n');
};

const buildSurveyResponsesCsv = (reportData) => {
  const questionOrder = Array.isArray(reportData?.questionOrder) ? reportData.questionOrder : [];
  const rows = Array.isArray(reportData?.surveyRows) ? reportData.surveyRows : [];
  const tableRows = [
    [
      'Name',
      'Call ID',
      'Date',
      ...questionOrder.map((q) => `${formatQuestionLabel(q)} Answer`),
      'Sentiment',
    ],
  ];

  rows.forEach((row) => {
    tableRows.push([
      row.name || '',
      row.callId || '',
      row.createdAt ? new Date(row.createdAt).toLocaleString() : '',
      ...questionOrder.map((qKey) => row.answers?.[qKey] || 'Not mentioned'),
      row.sentiment || 'Unknown',
    ]);
  });

  return tableRows.map((row) => row.map(escapeCsvField).join(',')).join('\n');
};

export const buildGeneralAnalyticsCsvData = (reportData) => {
  if (!reportData || reportData.reportType !== 'survey') {
    return {};
  }

  return {
    surveyStats: buildBatchStatsCsv(reportData.surveyStats || {}),
    surveyResponses: buildSurveyResponsesCsv(reportData),
  };
};

const getDateStamp = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString().slice(0, 10);
  }
  return date.toISOString().slice(0, 10);
};

export const buildGeneralAnalyticsCsvFilename = (csvKey, reportData) => {
  const csvOption = SURVEY_REPORT_CSV_OPTIONS.find((option) => option.key === csvKey);
  const prefix = csvOption?.filePrefix || 'analytics-report';
  return `${prefix}-${getDateStamp(reportData?.generatedAt || reportData?.createdAt)}.csv`;
};

export const downloadCsvText = (csvText, filename) => {
  if (!csvText) return;

  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const buildSurveyReportRows = ({ surveyCalls, surveyResults }) => (
  (Array.isArray(surveyCalls) ? surveyCalls : [])
    .map((log, idx) => {
      const analysis = surveyResults?.[log._id];
      if (!analysis) return null;

      const name = log.crmEntry?.contact?.fullName
        || [log.crmEntry?.contact?.firstName, log.crmEntry?.contact?.lastName].filter(Boolean).join(' ')
        || `Respondent ${idx + 1}`;

      const answers = {};
      (analysis.questions || []).forEach((question) => {
        const questionKey = question.questionNumber || question.question || question.answerCode || question.answer || '';
        answers[questionKey] = question.answer || 'Not mentioned';
      });

      return {
        logId: String(log._id),
        name,
        callId: log.callSid || log.callId || '',
        callType: getCallTypeLabel(log),
        createdAt: log.createdAt ? new Date(log.createdAt).toISOString() : '',
        sentiment: analysis.sentiment || 'Unknown',
        answers,
        transcript: log.transcript || '',
      };
    })
    .filter(Boolean)
);

export const buildGeneralAnalyticsReportData = ({
  reportType,
  scriptId,
  scriptName,
  scriptType,
  engine,
  dateRange,
  generatedAt,
  summaryStats = null,
  hospitalStats = null,
  surveyStats = null,
  sentimentStats = null,
  completionStats = null,
  questionOrder = [],
  surveyRows = [],
}) => ({
  reportVersion: GENERAL_ANALYTICS_REPORT_VERSION,
  reportType,
  scriptId: String(scriptId || '').trim(),
  scriptName: String(scriptName || '').trim(),
  scriptType: String(scriptType || '').trim(),
  engine: String(engine || '').trim(),
  generatedAt: generatedAt || new Date().toISOString(),
  dateRange: dateRange
    ? {
      from: dateRange.start instanceof Date ? dateRange.start.toISOString() : dateRange.start,
      to: dateRange.end instanceof Date ? dateRange.end.toISOString() : dateRange.end,
    }
    : null,
  summaryStats,
  hospitalStats,
  surveyStats,
  sentimentStats,
  completionStats,
  questionOrder,
  surveyRows,
});

const formatDateTime = (value) => {
  if (!value) return '';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return '';
  }
};

const formatDateOnly = (value) => {
  if (!value) return '';

  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return '';
  }
};

const renderQuestionLabel = (questionStat) => (
  questionStat.questionNumber
    ? `Q${String(questionStat.questionNumber).replace(/^Q/i, '')}`
    : 'Question'
);

const renderSummaryCards = (summaryStats) => {
  if (!summaryStats) return null;

  return (
    <div className="stat-cards">
      <div className="stat-card stat-total">
        <div className="label">Total Calls</div>
        <div className="value">{summaryStats.totalCalls}</div>
      </div>
      <div className="stat-card">
        <div className="label">{summaryStats.primaryRateLabel}</div>
        <div className="value">{summaryStats.primaryRateValue}%</div>
      </div>
      <div className="stat-card">
        <div className="label">{summaryStats.secondaryRateLabel}</div>
        <div className="value">{summaryStats.secondaryRateValue}%</div>
      </div>
      <div className="stat-card">
        <div className="label">{summaryStats.tertiaryRateLabel}</div>
        <div className="value">{summaryStats.tertiaryRateValue}%</div>
      </div>
      {summaryStats.quaternaryRateLabel && (
        <div className="stat-card">
          <div className="label">{summaryStats.quaternaryRateLabel}</div>
          <div className="value">{summaryStats.quaternaryRateValue}%</div>
        </div>
      )}
    </div>
  );
};

function GeneralAnalyticsReportComponent({
  reportData,
  csvData = {},
  downloadPulse = false,
  onClose = null,
}) {
  const [activeSurveyTab, setActiveSurveyTab] = useState('stats');
  const [transcriptModal, setTranscriptModal] = useState({
    open: false,
    title: '',
    transcript: '',
  });

  const isSurveyType = reportData?.reportType === 'survey';
  const surveyStats = reportData?.surveyStats || null;
  const surveyRows = Array.isArray(reportData?.surveyRows) ? reportData.surveyRows : [];
  const questionOrder = Array.isArray(reportData?.questionOrder) ? reportData.questionOrder : [];
  const hospitalStats = reportData?.hospitalStats || null;
  const summaryStats = reportData?.summaryStats || null;
  const sentimentStats = reportData?.sentimentStats || null;
  const completionStats = reportData?.completionStats || null;

  const availableSurveyCsvOptions = useMemo(
    () => SURVEY_REPORT_CSV_OPTIONS.filter((option) => csvData?.[option.key] || reportData),
    [csvData, reportData]
  );

  useEffect(() => {
    setActiveSurveyTab('stats');
    setTranscriptModal({ open: false, title: '', transcript: '' });
  }, [reportData]);

  useEffect(() => {
    if (!transcriptModal.open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setTranscriptModal({ open: false, title: '', transcript: '' });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [transcriptModal.open]);

  if (!reportData) {
    return null;
  }

  const handleDownloadSurveyCsv = (csvKey) => {
    const csvText = csvData?.[csvKey] || buildGeneralAnalyticsCsvData(reportData)?.[csvKey];
    if (!csvText) return;

    downloadCsvText(csvText, buildGeneralAnalyticsCsvFilename(csvKey, reportData));
  };

  const renderBreakdown = (key, stat) => {
    const total = Object.values(stat.answers || {}).reduce((sum, value) => sum + value, 0);

    return (
      <div key={key} className="question-stat">
        <div>
          <div className="question-meta">Metric</div>
          <div className="question-title">{stat.title}</div>
          <div className="question-text">{stat.description}</div>
          <div className="question-meta">Total calls: {total}</div>
        </div>
        <div className="answer-and-chart">
          <div className="answer-list">
            {Object.entries(stat.answers || {})
              .sort((a, b) => b[1] - a[1])
              .map(([answer, count]) => (
                <div key={answer} className="answer-row">
                  <span className="answer">{answer}</span>
                  <span className="percent">{buildPercent(count, total)}%</span>
                </div>
              ))}
          </div>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart margin={{ top: 4, right: 8, bottom: 28, left: 8 }}>
                <Pie
                  data={Object.entries(stat.answers || {}).map(([name, value]) => ({ name, value }))}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="42%"
                  outerRadius="78%"
                  paddingAngle={1}
                >
                  {Object.keys(stat.answers || {}).map((name, idx) => (
                    <Cell key={name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value} calls`} />
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ fontSize: 12, paddingTop: 6 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="general-analytics-report">
        <div className="general-analytics-report__meta">
          <div className="general-analytics-report__meta-row">
            <div>
              <div className="general-analytics-report__title">
                {reportData.scriptName || 'Analytics report'}
              </div>
              <div className="general-analytics-report__subtitle">
                Generated at: {formatDateTime(reportData.generatedAt)}
                {reportData.dateRange?.from && reportData.dateRange?.to && (
                  <> | Report range: {formatDateOnly(reportData.dateRange.from)} - {formatDateOnly(reportData.dateRange.to)}</>
                )}
              </div>
            </div>
            {typeof onClose === 'function' && (
              <button
                type="button"
                className="general-analytics-report__close"
                onClick={onClose}
              >
                Close
              </button>
            )}
          </div>
        </div>

        {!isSurveyType && renderSummaryCards(summaryStats)}

        {isSurveyType && surveyStats && (
          <div className="batch-stats" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="batch-stats__header" style={{ flex: '0 0 auto' }}>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', borderBottom: '2px solid #e5e7eb' }}>
                <button
                  type="button"
                  onClick={() => setActiveSurveyTab('stats')}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: 'none',
                    borderBottom: activeSurveyTab === 'stats' ? '3px solid #3b82f6' : '3px solid transparent',
                    color: activeSurveyTab === 'stats' ? '#3b82f6' : '#6b7280',
                    fontWeight: activeSurveyTab === 'stats' ? '600' : '400',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                  }}
                >
                  Statistics
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSurveyTab('table')}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: 'none',
                    borderBottom: activeSurveyTab === 'table' ? '3px solid #3b82f6' : '3px solid transparent',
                    color: activeSurveyTab === 'table' ? '#3b82f6' : '#6b7280',
                    fontWeight: activeSurveyTab === 'table' ? '600' : '400',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                  }}
                >
                  Response Table
                </button>
              </div>

              <div className="general-analytics-report__downloads">
                {availableSurveyCsvOptions.map((csvOption) => (
                  <button
                    key={csvOption.key}
                    className={`batch-button batch-button--accent${downloadPulse ? ' batch-button--pulse' : ''}`}
                    onClick={() => handleDownloadSurveyCsv(csvOption.key)}
                  >
                    <span className="batch-button__label">{csvOption.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="batch-stats__content" style={{ flex: '1 1 auto', overflow: 'auto', width: '100%' }}>
              {activeSurveyTab === 'table' && (
                <div style={{ width: '100%' }}>
                  <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Name</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Call ID</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Date</th>
                        {questionOrder.map((qKey) => (
                          <th
                            key={qKey}
                            style={{
                              padding: '0.75rem 1rem',
                              textAlign: 'left',
                              fontWeight: '600',
                              fontSize: '0.875rem',
                              color: '#374151',
                            }}
                          >
                            {`${formatQuestionLabel(qKey)} Answer`}
                          </th>
                        ))}
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Sentiment</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Transcript</th>
                      </tr>
                    </thead>
                    <tbody>
                      {surveyRows.map((row) => (
                        <tr
                          key={row.logId}
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            transition: 'background-color 0.15s',
                          }}
                          onMouseEnter={(event) => { event.currentTarget.style.backgroundColor = '#f9fafb'; }}
                          onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}>{row.name}</td>
                          <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontFamily: 'monospace', color: '#6b7280' }}>
                            {row.callId ? `${row.callId.substring(0, 12)}...` : ''}
                          </td>
                          <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                            {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : ''}
                          </td>
                          {questionOrder.map((qKey) => (
                            <td
                              key={qKey}
                              style={{
                                padding: '0.75rem 1rem',
                                fontSize: '0.875rem',
                                maxWidth: '200px',
                                wordWrap: 'break-word',
                              }}
                            >
                              {row.answers?.[qKey] || 'Not mentioned'}
                            </td>
                          ))}
                          <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}>
                            <span
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                textTransform: 'capitalize',
                                backgroundColor: row.sentiment === 'positive' ? '#d1fae5' : row.sentiment === 'negative' ? '#fee2e2' : '#e5e7eb',
                                color: row.sentiment === 'positive' ? '#065f46' : row.sentiment === 'negative' ? '#991b1b' : '#374151',
                              }}
                            >
                              {row.sentiment || 'Unknown'}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}>
                            <button
                              type="button"
                              className="table-transcript-button"
                              disabled={!row.transcript}
                              onClick={() => {
                                if (!row.transcript) return;
                                setTranscriptModal({
                                  open: true,
                                  title: row.name,
                                  transcript: row.transcript || 'No transcript available.',
                                });
                              }}
                            >
                              Show
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {surveyRows.length === 0 && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                      No responses with analysis data available.
                    </div>
                  )}
                </div>
              )}

              {activeSurveyTab === 'stats' && (
                <div>
                  {Object.values(surveyStats)
                    .sort(compareQuestionStats)
                    .map((questionStat) => (
                      <div key={questionStat.questionNumber || questionStat.question} className="question-stat">
                        <div>
                          <div className="question-meta">Question</div>
                          <div className="question-title">{renderQuestionLabel(questionStat)}</div>
                          <div className="question-text">{questionStat.question}</div>
                          <div className="question-meta">Responses: {questionStat.total}</div>
                        </div>
                        <div className="answer-and-chart">
                          <div className="answer-list">
                            {Object.entries(questionStat.answers || {})
                              .sort((a, b) => b[1] - a[1])
                              .map(([answer, count]) => (
                                <div key={answer} className="answer-row">
                                  <span className="answer">{answer}</span>
                                  <span className="percent">{buildPercent(count, questionStat.total)}%</span>
                                </div>
                              ))}
                          </div>
                          <div className="chart-wrapper">
                            <ResponsiveContainer width="100%" height={240}>
                              <PieChart margin={{ top: 4, right: 8, bottom: 28, left: 8 }}>
                                <Pie
                                  data={Object.entries(questionStat.answers || {}).map(([name, value]) => ({ name, value }))}
                                  dataKey="value"
                                  nameKey="name"
                                  innerRadius="42%"
                                  outerRadius="78%"
                                  paddingAngle={1}
                                >
                                  {Object.keys(questionStat.answers || {}).map((name, idx) => (
                                    <Cell key={name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => `${value} responses`} />
                                <Legend
                                  layout="horizontal"
                                  verticalAlign="bottom"
                                  align="center"
                                  wrapperStyle={{ fontSize: 12, paddingTop: 6 }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    ))}

                  {sentimentStats && (
                    <div className="sentiment-stat">
                      <div>
                        <div className="question-meta">Sentiment</div>
                        <div className="question-title">Respondent Attitude</div>
                        <div className="question-text">
                          Breakdown of sentiment across analyzed calls.
                        </div>
                      </div>
                      <div className="answer-and-chart">
                        <div className="answer-list">
                          {Object.entries(sentimentStats)
                            .filter(([, count]) => count > 0)
                            .sort((a, b) => b[1] - a[1])
                            .map(([sentiment, count]) => {
                              const total = Object.values(sentimentStats).reduce((sum, value) => sum + value, 0);
                              return (
                                <div key={sentiment} className="answer-row">
                                  <span className="answer">{sentiment}</span>
                                  <span className="percent">{buildPercent(count, total)}%</span>
                                </div>
                              );
                            })}
                        </div>
                        <div className="chart-wrapper">
                          <ResponsiveContainer width="100%" height={240}>
                            <PieChart margin={{ top: 4, right: 8, bottom: 28, left: 8 }}>
                              <Pie
                                data={Object.entries(sentimentStats)
                                  .filter(([, value]) => value > 0)
                                  .map(([name, value]) => ({ name, value }))}
                                dataKey="value"
                                nameKey="name"
                                innerRadius="42%"
                                outerRadius="78%"
                                paddingAngle={1}
                              >
                                {Object.entries(sentimentStats)
                                  .filter(([, value]) => value > 0)
                                  .map(([name], idx) => (
                                    <Cell key={name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                  ))}
                              </Pie>
                              <Tooltip formatter={(value) => `${value} calls`} />
                              <Legend
                                layout="horizontal"
                                verticalAlign="bottom"
                                align="center"
                                wrapperStyle={{ fontSize: 12, paddingTop: 6 }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  )}

                  {completionStats && (
                    <div className="completion-stat">
                      <div>
                        <div className="question-meta">Percent Completion</div>
                        <div className="question-title">Completion Rate</div>
                        <div className="question-text">
                          Distribution of respondents by percent of questions completed.
                        </div>
                      </div>
                      <div className="answer-and-chart">
                        <div className="answer-list">
                          {Object.entries(completionStats)
                            .filter(([, count]) => count > 0)
                            .sort((a, b) => Number(b[0]) - Number(a[0]))
                            .map(([percent, count]) => (
                              <div key={percent} className="answer-row">
                                <span className="answer">{percent}%</span>
                                <span className="percent">{count}</span>
                              </div>
                            ))}
                        </div>
                        <div className="chart-wrapper">
                          <ResponsiveContainer width="100%" height={240}>
                            <PieChart margin={{ top: 4, right: 8, bottom: 28, left: 8 }}>
                              <Pie
                                data={Object.entries(completionStats)
                                  .filter(([, value]) => value > 0)
                                  .map(([name, value]) => ({ name: `${name}%`, value }))}
                                dataKey="value"
                                nameKey="name"
                                innerRadius="42%"
                                outerRadius="78%"
                                paddingAngle={1}
                              >
                                {Object.entries(completionStats)
                                  .filter(([, value]) => value > 0)
                                  .map(([name], idx) => (
                                    <Cell key={name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                  ))}
                              </Pie>
                              <Tooltip formatter={(value) => `${value} respondents`} />
                              <Legend
                                layout="horizontal"
                                verticalAlign="bottom"
                                align="center"
                                wrapperStyle={{ fontSize: 12, paddingTop: 6 }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {!isSurveyType && hospitalStats && (
          <div className="batch-stats" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="batch-stats__header" style={{ flex: '0 0 auto' }}>
              <h3>{reportData.scriptName || 'Selected Script'} Insights</h3>
            </div>
            <div
              data-scroll-container
              className="scroll-container"
              style={{ flex: '1 1 auto', overflow: 'auto', width: '100%' }}
            >
              {Object.entries(hospitalStats).map(([key, stat]) => renderBreakdown(key, stat))}
            </div>
          </div>
        )}
      </div>

      {transcriptModal.open && (
        <div
          className="transcript-modal__backdrop"
          onClick={() => setTranscriptModal({ open: false, title: '', transcript: '' })}
        >
          <div
            className="transcript-modal__content"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="transcript-modal__header">
              <div>
                <div className="transcript-modal__title">Transcript</div>
                {transcriptModal.title && (
                  <div className="transcript-modal__subtitle">{transcriptModal.title}</div>
                )}
              </div>
              <button
                type="button"
                className="transcript-modal__close"
                onClick={() => setTranscriptModal({ open: false, title: '', transcript: '' })}
              >
                x
              </button>
            </div>
            <div className="transcript-modal__body">
              <pre>{transcriptModal.transcript}</pre>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default GeneralAnalyticsReportComponent;
